import {authorityApi,deviceAuthorityApi} from '../../lib/api';
import {runLoginCeremony} from '../../lib/webauthn';
import {unlockSecurityAuthority} from '../../lib/securityAuthority';
import {providerMarketApi} from '../../lib/providerMarketApi';
import {freshStepUpProof} from '../../lib/identityAccessApi';

export type Data=Record<string,any>;
export type Activity={id:string;occurred_at:string;kind:string;provider_key:string|null;model_key:string|null;status:string;duration_ms:number|null;detail:Data};
export type Page={observed_at:string;tenant_id:string;items:Activity[];has_more:boolean;next_cursor:string|null};
export type Snapshot={observed_at:string;tenant_id:string;providers:Data[];models:Data[];policies:Data[];profiles:Data[];capabilities:Data};
export class CenterError extends Error {status:number;constructor(status:number,message:string){super(message);this.status=status;}}

export async function request<T>(path:string,signal?:AbortSignal,body?:Data,timeoutMs=25000):Promise<T>{
  const abort=new AbortController(),cancel=()=>abort.abort();
  signal?.addEventListener('abort',cancel,{once:true});
  if(signal?.aborted)abort.abort();
  const timer=setTimeout(cancel,timeoutMs);
  try{
    const response=await fetch(path,{credentials:'include',cache:'no-store',signal:abort.signal,
      method:body?'POST':'GET',headers:{Accept:'application/json','Content-Type':'application/json'},
      ...(body?{body:JSON.stringify(body)}:{})});
    if(!response.ok){let code='request_failed_'+response.status;
      try{const data=await response.json();if(typeof data?.detail==='string')code=data.detail;}catch{}
      throw new CenterError(response.status,code);
    }
    return await response.json() as T;
  }finally{clearTimeout(timer);signal?.removeEventListener('abort',cancel);}
}

const base='/api/v1/provider-market/center';
const query=(args:Record<string,string|undefined>)=>new URLSearchParams(Object.entries(args).filter((row):row is [string,string]=>!!row[1])).toString();
export const centerApi={
  governanceHealth:(signal?:AbortSignal)=>request<Data>('/api/v1/provider-market/governance/health',signal),
  governanceMap:(signal?:AbortSignal)=>request<Data>('/api/v1/provider-market/governance/map',signal),
  executionExplain:(executionId:string,signal?:AbortSignal)=>request<Data>('/api/v1/provider-market/governance/gateway-executions/'+encodeURIComponent(executionId)+'/explain',signal),
  workItemExplain:(workItemId:string,signal?:AbortSignal)=>request<Data>('/api/v1/provider-market/governance/work-items/'+encodeURIComponent(workItemId)+'/explain',signal),
  async test(provider:string,model:string,profile:string,latencyLimit:number,repeats:number,requestId:string){
    const proof=await freshStepUpProof();
    return request<Data>(base+'/tests/verify',undefined,{provider_key:provider,model_key:model,
      profile,latency_limit_ms:latencyLimit,repeats,request_id:requestId,...proof},230000);
  },
  snapshot:(signal?:AbortSignal)=>request<Snapshot>(base+'/snapshot',signal),
  activity:(args:Record<string,string|undefined>,signal?:AbortSignal)=>request<Page>(base+'/activity?'+query(args),signal),
  model:(provider:string,model:string,signal?:AbortSignal)=>request<Data>(base+'/model?'+query({provider,model}),signal),
  inspection:(policy:string,task:string,signal?:AbortSignal)=>request<Data>(base+'/inspection?'+query({policy,task}),signal),
  decisions:(signal?:AbortSignal)=>request<{items:Data[]}>(base+'/decisions',signal),
  memory:(signal?:AbortSignal)=>request<Data>('/api/v1/memory/provider-model-scope',signal),
  async unlock(){
    // Sign into the existing Device Authority on this origin. Do not use the legacy HTML page.
    try{await deviceAuthorityApi.me();}
    catch(error){
      const msg=error instanceof Error?error.message:'';
      if(!/401|session|authentication|sign.in/i.test(msg))throw error;
      const options=await deviceAuthorityApi.loginOptions();
      await deviceAuthorityApi.loginVerify(await runLoginCeremony(options));
    }
    await unlockSecurityAuthority();
    await authorityApi.status();
  },
  async enable(provider:string,model:string,enabled:boolean,reason:string){
    const result=await providerMarketApi.setDeploymentEnabled({providerKey:provider,deploymentKey:model,enabled,reason});
    const value=result as Data;
    if(value.updated!==true||value.result?.provider_key!==provider||value.result?.deployment_key!==model||value.result?.enabled!==enabled)
      throw new Error('model_enablement_result_not_verified');
    const after=await centerApi.model(provider,model);
    if(after.enabled!==enabled||after.pm?.enabled!==enabled)throw new Error('model_enablement_readback_failed');
  },
  async connectStored(provider:string,reason:string){
    const proof=await freshStepUpProof();
    const response=await request<Data>('/api/v1/provider-market/connections/connect-stored/verify',undefined,
      {provider_key:provider,reason,...proof});
    if(response.updated!==true || response.result?.gateway_sync?.updated!==true || response.result?.gateway_sync?.gateway_source!=='VAULT')
      throw new Error('provider_vault_gateway_sync_unverified');
    // A confirmed server receipt is the authoritative Provider Market write result.
    // The read endpoint may redact credential_ref; do not require a secret reference in the browser.
    const receipt=response.result?.connection;
    if(receipt?.provider_key!==provider||receipt?.connection_status!=='CONFIGURED'||receipt?.credential_ref_class!=='VAULT')
      throw new Error('provider_vault_market_receipt_unverified');
    const after=await providerMarketApi.connections(provider);
    if(after.connection && (after.connection.connection_status!=='CONFIGURED'||
      (typeof after.connection.credential_ref==='string' && !after.connection.credential_ref.startsWith('VAULT:'))))
      throw new Error('provider_vault_market_readback_conflict');
    const latest=await centerApi.snapshot();
    const row=latest.providers.find(p=>p.provider_key===provider);
    if(row?.runtime_credential_source!=='VAULT')
      throw new Error('provider_vault_gateway_readback_failed');
  },
  async connect(provider:string,credentialRef:string,reason:string){
    if(!/^ENV:[A-Z_][A-Z0-9_]{0,100}$/.test(credentialRef))throw new Error('existing_runtime_env_reference_required');
    await providerMarketApi.connectProvider({providerKey:provider,credentialRef,reason});
    const after=await providerMarketApi.connections(provider);
    if(after.connection?.credential_ref!==credentialRef)throw new Error('provider_connection_readback_failed');
  },
  async disconnect(provider:string,reason:string){
    const proof=await freshStepUpProof();
    const value=await request<Data>('/api/v1/provider-market/connections/disconnect/verify',undefined,{provider_key:provider,reason,...proof});
    if(value.updated!==true)throw new Error('provider_disconnect_not_verified');
    const after=await providerMarketApi.connections(provider);
    if(after.connection?.connection_status==='CONNECTED')throw new Error('provider_disconnect_readback_failed');
  },
  async approveMemory(model:string,reviewed:Data){
    const current=await centerApi.memory();
    if(current.tenant_id!==reviewed.tenant_id||current.subject_id!==reviewed.subject_id||current.fingerprint!==reviewed.fingerprint||current.policy?.id!==reviewed.policy?.id)
      throw new Error('memory_scope_changed_review_again');
    const upstream=current.candidates?.[model];
    const detail=await centerApi.model('openrouter',model);
    if(detail.pm?.enabled!==true)throw new Error('Memory onayından önce tenant model kullanım iznini açın.');
    if(detail.enabled!==true)throw new Error('Gateway model kaydı kapalı. Model kullanım iznini yeniden inceleyin.');
    if(!upstream)throw new Error('Model mevcut Memory aday kapsamında değil.');
    if(detail.upstream_model!==upstream)throw new Error('Memory adayının model kimliği eşleşmiyor.');
    if(/minimax/i.test(model))throw new Error('Bu model için Memory kapsamı genişletilemez.');
    if(current.authority_allowed!==true||current.policy_fresh!==true||current.policy_scope_valid!==true)throw new Error('memory_scope_authority_or_freshness_required');
    const proof=await freshStepUpProof();
    const response=await request<Data>('/api/v1/memory/provider-model-scope/verify',undefined,
      {policy_id:current.policy.id,expected_fingerprint:current.fingerprint,models:[model],...proof});
    if(response.verified!==true||response.result?.policy_id!==current.policy.id||!response.result?.approved_models?.includes(model))throw new Error('memory_approval_not_verified');
    const after=await centerApi.memory();
    if(after.tenant_id!==current.tenant_id||after.policy?.id!==current.policy.id||!after.policy.approved_models?.includes(model))throw new Error('memory_approval_readback_failed');
  },
};
